import { useState, type ReactNode } from "react";
import { Menu, X } from "lucide-react";
import { cn } from "../lib/cn";

export interface NavItem {
  key: string;
  label: string;
  href: string;
  icon?: ReactNode;
  badge?: string | number;
}

export interface NavGroup {
  key: string;
  label: string;
  items: NavItem[];
}

export interface AppShellProps {
  surface: "console" | "ops";
  productName: string;
  nav: NavGroup[];
  activeKey?: string;
  onNavigate?: (href: string) => void;
  /** Up to four items get a bottom bar on phones; the rest live behind the menu. */
  primaryNavKeys?: string[];
  headerRight?: ReactNode;
  children: ReactNode;
}

/**
 * Mobile first, and literally so: the base layout is the phone one, and the
 * sidebar only appears from `md`. There is no mobile app for these consoles, so
 * a phone is a first-class target rather than a narrow desktop.
 *
 * On a phone the primary destinations sit in a thumb-reachable bottom bar and
 * everything else is behind a slide-over. On a desktop the same nav renders as a
 * persistent sidebar. One nav definition, two presentations.
 */
export function AppShell({
  surface,
  productName,
  nav,
  activeKey,
  onNavigate,
  primaryNavKeys,
  headerRight,
  children,
}: AppShellProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const allItems = nav.flatMap((group) => group.items);
  const bottomItems = primaryNavKeys
    ? primaryNavKeys.map((key) => allItems.find((item) => item.key === key)).filter(Boolean).slice(0, 4)
    : allItems.slice(0, 4);

  const go = (href: string) => {
    setMenuOpen(false);
    onNavigate?.(href);
  };

  return (
    <div className="flex min-h-dvh flex-col bg-[var(--surface-page)] md:flex-row">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-[var(--border-subtle)] bg-[var(--surface-card)] md:flex">
        <Brand surface={surface} productName={productName} />
        <NavList nav={nav} activeKey={activeKey} onNavigate={go} />
      </aside>

      {menuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-[var(--color-ink-950)]/50"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-[var(--surface-card)] shadow-[var(--shadow-overlay)]">
            <div className="flex items-center justify-between pr-2">
              <Brand surface={surface} productName={productName} />
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setMenuOpen(false)}
                className="grid size-11 place-items-center rounded-[var(--radius-control)] text-[var(--text-secondary)]"
              >
                <X className="size-5" />
              </button>
            </div>
            <NavList nav={nav} activeKey={activeKey} onNavigate={go} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-[var(--border-subtle)] bg-[var(--surface-card)] px-2 md:px-4">
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setMenuOpen(true)}
            className="grid size-11 place-items-center rounded-[var(--radius-control)] text-[var(--text-secondary)] md:hidden"
          >
            <Menu className="size-5" />
          </button>
          <span className="truncate text-sm font-medium text-[var(--text-primary)] md:hidden">
            {productName}
          </span>
          <div className="ml-auto flex items-center gap-1">{headerRight}</div>
        </header>

        <main className="min-w-0 flex-1 px-3 pb-24 pt-3 md:px-6 md:pb-8 md:pt-5">{children}</main>

        {bottomItems.length > 1 && (
          <nav
            aria-label="Primary"
            className="fixed inset-x-0 bottom-0 z-30 flex border-t border-[var(--border-subtle)] bg-[var(--surface-card)] pb-[env(safe-area-inset-bottom)] md:hidden"
          >
            {bottomItems.map((item) => {
              if (!item) return null;
              const active = item.key === activeKey;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => go(item.href)}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 px-1 text-2xs font-medium",
                    active ? "text-[var(--text-brand)]" : "text-[var(--text-muted)]",
                  )}
                >
                  <span className="relative grid place-items-center">
                    {item.icon}
                    {item.badge != null && (
                      <span className="absolute -right-2 -top-1 min-w-4 rounded-[var(--radius-pill)] bg-danger-600 px-1 text-center text-2xs font-semibold text-white">
                        {item.badge}
                      </span>
                    )}
                  </span>
                  <span className="max-w-full truncate">{item.label}</span>
                </button>
              );
            })}
          </nav>
        )}
      </div>
    </div>
  );
}

function Brand({ surface, productName }: { surface: "console" | "ops"; productName: string }) {
  return (
    <div className="flex h-14 items-center gap-2 px-4">
      <span
        className={cn(
          "grid size-7 shrink-0 place-items-center rounded-[var(--radius-control)] text-xs font-bold text-white",
          surface === "ops" ? "bg-ink-800" : "bg-[var(--accent-solid)]",
        )}
      >
        N
      </span>
      <span className="truncate text-sm font-semibold tracking-tight text-[var(--text-primary)]">
        {productName}
      </span>
      {surface === "ops" && (
        <span className="ml-auto rounded-[var(--radius-pill)] bg-warning-500/15 px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wide text-warning-600">
          Ops
        </span>
      )}
    </div>
  );
}

function NavList({
  nav,
  activeKey,
  onNavigate,
}: {
  nav: NavGroup[];
  activeKey?: string;
  onNavigate: (href: string) => void;
}) {
  return (
    <nav className="flex-1 overflow-y-auto px-2 pb-4">
      {nav.map((group) => (
        <div key={group.key} className="mb-4">
          <div className="px-2 pb-1 text-2xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">
            {group.label}
          </div>
          <ul className="space-y-0.5">
            {group.items.map((item) => {
              const active = item.key === activeKey;
              return (
                <li key={item.key}>
                  <button
                    type="button"
                    onClick={() => onNavigate(item.href)}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex min-h-11 w-full items-center gap-2 rounded-[var(--radius-control)] px-2 text-left text-sm transition-colors",
                      active
                        ? "bg-[var(--accent-soft)] font-medium text-[var(--accent-soft-text)]"
                        : "text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]",
                    )}
                  >
                    {item.icon}
                    <span className="truncate">{item.label}</span>
                    {item.badge != null && (
                      <span className="ml-auto rounded-[var(--radius-pill)] bg-[var(--surface-sunken)] px-1.5 text-2xs font-medium text-[var(--text-secondary)]">
                        {item.badge}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}
